import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * The address book in the public shop API: a signed-in customer's saved addresses, which checkout
 * (#89) offers to bill and ship to. Any address can be used for both; `defaultBilling` and
 * `defaultShipping` say which one checkout picks first. Registering stores its address as the
 * default for both. Every endpoint needs the customer session (401 `CustomerNotSignedIn`); an
 * address of someone else is `AddressNotFound` (404), as if it did not exist.
 */

/** The countries the shop delivers to and invoices in. */
export const AddressCountry = z.enum(["NL", "BE"]);
export type AddressCountry = z.infer<typeof AddressCountry>;

/** The postcode written the way the country writes it, or undefined when it does not fit the country. */
const postcodeFor = (country: AddressCountry, typed: string): string | undefined => {
	const compact = typed.replace(/\s/g, "").toUpperCase();
	if (country === "NL" && /^[1-9]\d{3}[A-Z]{2}$/.test(compact))
		return `${compact.slice(0, 4)} ${compact.slice(4)}`;
	if (country === "BE" && /^[1-9]\d{3}$/.test(compact)) return compact;
	return undefined;
};

/** An optional line of text: spaces around it dropped, empty means none (null). */
const optionalLine = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullish()
		.transform((value) => (value ? value : null));

/** The fields of an address, each checked on its own. */
export const AddressFields = z.object({
	/** Who the post is for ("t.a.v."); empty means the customer themselves (`recipientOf`). */
	recipientName: optionalLine(200),
	/** The company on the address; empty means none. */
	companyName: optionalLine(200),
	street: z.string().trim().min(1).max(200),
	houseNumber: z
		.string()
		.trim()
		.regex(/^\d{1,6}$/),
	/** `A`, `bis`, `-2`; empty means none. */
	houseNumberAddition: optionalLine(10),
	postcode: z.string().trim().min(1).max(20),
	city: z.string().trim().min(1).max(100),
	country: AddressCountry,
});

/** Checks the postcode against the country and writes it the country's way. */
export const withPostcode = <S extends z.ZodType<z.output<typeof AddressFields>>>(schema: S) =>
	schema.transform((address, context) => {
		const postcode = postcodeFor(address.country, address.postcode);
		if (postcode === undefined) {
			context.addIssue({
				code: "invalid_format",
				format: "regex",
				path: ["postcode"],
				input: address.postcode,
			});
			return z.NEVER;
		}
		return { ...address, postcode };
	});

/**
 * An address as the customer types it. The postcode must fit the country (NL `1234 AB`, BE
 * `1000`) and is stored the way it is written there; the house number is digits only, with any
 * letter or `-2` in the addition.
 */
export const AddressInput = withPostcode(AddressFields);
/** What a form sends. */
export type AddressInput = z.input<typeof AddressInput>;
/** What the API stores: postcode written the country's way, no addition as null. */
export type Address = z.output<typeof AddressInput>;

/** A saved address. */
export const CustomerAddress = z.object({
	id: z.string(),
	/** As the customer typed it; null means the customer's own name (`recipientOf`). */
	recipientName: z.string().nullable(),
	/** Null: no company on the address. */
	companyName: z.string().nullable(),
	street: z.string(),
	houseNumber: z.string(),
	houseNumberAddition: z.string().nullable(),
	postcode: z.string(),
	city: z.string(),
	country: AddressCountry,
	/** The address checkout bills to first. At most one per customer. */
	defaultBilling: z.boolean(),
	/** The address checkout ships to first. At most one per customer. */
	defaultShipping: z.boolean(),
});
export type CustomerAddress = z.infer<typeof CustomerAddress>;

/**
 * Who an address is for: its recipient ("t.a.v."), or the customer's own name when it has none.
 * What a label, an invoice or an order's snapshot puts on the name line (null when neither is known).
 */
export const recipientOf = (
	address: { readonly recipientName: string | null },
	customerName: string | null,
): string | null => address.recipientName ?? customerName;

/**
 * The company an address is for: its own company name, or, when it has none, the company name of
 * the customer's approved business profile (`businessCompanyName`, null for anyone else). What an
 * invoice, a label or an order's snapshot puts on the company line (null: none).
 */
export const companyOf = (
	address: { readonly companyName: string | null },
	businessCompanyName: string | null,
): string | null => address.companyName ?? businessCompanyName;

/** The customer's addresses: the defaults first, then oldest first. */
export const CustomerAddressList = z.array(CustomerAddress);
export type CustomerAddressList = z.infer<typeof CustomerAddressList>;

export const AddressPath = z.object({ addressId: z.string() });

export const UpdateAddressInput = withPostcode(AddressPath.extend(AddressFields.shape));
export type UpdateAddressInput = z.input<typeof UpdateAddressInput>;

/** What a default is for. */
export const AddressDefaultKind = z.enum(["billing", "shipping"]);
export type AddressDefaultKind = z.infer<typeof AddressDefaultKind>;

export const SetDefaultAddressBody = z.object({ kind: AddressDefaultKind });
export type SetDefaultAddressBody = z.infer<typeof SetDefaultAddressBody>;

/** How many addresses one customer can save. */
export const MAX_ADDRESSES = 20;

export const CustomerAddressContract = {
	/** The signed-in customer's addresses. */
	list: oc.route({ method: "GET", path: "/shop/account/addresses" }).output(CustomerAddressList),
	/**
	 * Saves an address. It becomes the default for billing and for shipping where the customer has
	 * none. Errors: `AddressLimitReached` (409, at most `MAX_ADDRESSES`).
	 */
	add: oc
		.route({ method: "POST", path: "/shop/account/addresses", successStatus: 201 })
		.input(AddressInput)
		.output(CustomerAddress),
	/**
	 * Changes an address; its defaults stay. Orders placed with it keep their own copy. Errors:
	 * `AddressNotFound` (404).
	 */
	update: oc
		.route({ method: "PUT", path: "/shop/account/addresses/{addressId}" })
		.input(UpdateAddressInput)
		.output(CustomerAddress),
	/**
	 * Deletes an address. When it was a default, the most recently added remaining address becomes
	 * the default for that kind (billing, shipping, or both); after the last address there is none.
	 * Errors: `AddressNotFound` (404).
	 */
	remove: oc
		.route({ method: "DELETE", path: "/shop/account/addresses/{addressId}", successStatus: 204 })
		.input(AddressPath),
	/**
	 * Makes the address the default for billing or for shipping, in place of the previous one, and
	 * answers with all addresses. Errors: `AddressNotFound` (404).
	 */
	setDefault: oc
		.route({ method: "POST", path: "/shop/account/addresses/{addressId}/default" })
		.input(AddressPath.extend(SetDefaultAddressBody.shape))
		.output(CustomerAddressList),
};
